<template>
  <Teleport to="body">
    <transition name="fade">
      <div v-if="visible" class="sponsor-modal__backdrop">
        <section class="sponsor-modal">
          <header class="sponsor-modal__header">
            <h3 class="sponsor-modal__title">{{ t('miniApp.welcomeTitle') }}</h3>
          </header>

          <div class="sponsor-modal__body">
            <div class="sponsor-modal__welcome">
              <div class="sponsor-modal__avatar">
                <i class="zmdi zmdi-account-circle" />
              </div>
              <p v-if="telegramUser?.first_name" class="sponsor-modal__greeting">
                {{ t('miniApp.helloUser', { name: telegramUser.first_name }) }}
              </p>
            </div>

            <p class="sponsor-modal__hint">
              {{ t('miniApp.enterSponsorHint') }}
            </p>

            <form @submit.prevent="submit" class="sponsor-modal__form">
              <div class="sponsor-modal__field">
                <input
                  ref="inputRef"
                  v-model="sponsorLogin"
                  type="text"
                  class="sponsor-modal__input"
                  :placeholder="t('miniApp.sponsorPlaceholder')"
                  :disabled="isLoading"
                  autocomplete="off"
                  @keydown.enter="submit"
                />
              </div>

              <Transition name="fade">
                <p v-if="error" class="sponsor-modal__error">
                  <i class="zmdi zmdi-alert-circle" />
                  {{ error }}
                </p>
              </Transition>

              <button
                type="submit"
                class="sponsor-modal__button"
                :disabled="!isValid || isLoading"
              >
                <i v-if="isLoading" class="zmdi zmdi-spinner zmdi-hc-spin" />
                {{ isLoading ? t('common.loading') : t('common.continue') }}
              </button>
            </form>
          </div>
        </section>
      </div>
    </transition>
  </Teleport>
</template>

<script setup lang="ts">
import { ref, computed, nextTick, watch, onUnmounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { TelegramAuthService } from './TelegramAuthService'
import type { TelegramAuthResult } from './TelegramAuthService'

interface Props {
  visible: boolean
  initData: string
  telegramUser?: {
    id: number
    first_name: string
    last_name?: string
    username?: string
  } | null
}

interface Emits {
  (e: 'result', result: TelegramAuthResult): void
  (e: 'close'): void
}

const props = defineProps<Props>()
const emit = defineEmits<Emits>()
const { t } = useI18n()

const sponsorLogin = ref('')
const isLoading = ref(false)
const error = ref('')
const inputRef = ref<HTMLInputElement | null>(null)

const isValid = computed(() => sponsorLogin.value.trim().length >= 3)
let generation = 0
onUnmounted(() => { ++generation })

async function submit() {
  if (!props.visible || !isValid.value || isLoading.value) return
  const current = ++generation

  isLoading.value = true
  error.value = ''

  try {
    const result = await TelegramAuthService.register(
      props.initData,
      sponsorLogin.value.trim()
    )

    if (current !== generation || !props.visible) return
    if (result.result === 'error') error.value = t('miniApp.sponsorNotFound')
    else emit('result', result)
  } catch {
    if (current === generation && props.visible) error.value = t('errors.networkError')
  } finally {
    if (current === generation) isLoading.value = false
  }
}

// Auto-focus input when modal becomes visible
watch(() => [props.visible, props.initData], ([visible]) => {
  ++generation
  isLoading.value = false
  if (visible) {
    sponsorLogin.value = ''
    error.value = ''
    nextTick(() => inputRef.value?.focus())
  }
})
</script>

<style scoped lang="scss">
.sponsor-modal__backdrop {
  position: fixed;
  inset: 0;
  background: var(--ds-scrim);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2100;
}

.sponsor-modal {
  width: min(420px, 90%);
  background: var(--ds-text-on-accent);
  border-radius: var(--ds-radius-modal);
  box-shadow: 0 24px 48px var(--ds-scrim);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  animation: slideIn 0.3s ease;
}

.sponsor-modal__header {
  padding: 24px 24px 0;
  text-align: center;
}

.sponsor-modal__title {
  margin: 0;
  font-size: var(--ds-text-size-h-sm);
  font-weight: var(--ds-weight-bold);
  color: var(--hm-text-primary);
}

.sponsor-modal__body {
  padding: 16px 24px 32px;
}

.sponsor-modal__welcome {
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: 20px;
}

.sponsor-modal__avatar {
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: var(--hm-accent-soft, var(--ds-accent-soft-08));
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 8px;

  i {
    font-size: var(--p-text-32);
    color: var(--hm-accent);
  }
}

.sponsor-modal__greeting {
  font-size: var(--ds-text-size-body-lg);
  font-weight: var(--ds-weight-semibold);
  color: var(--hm-text-primary);
  margin: 0;
}

.sponsor-modal__hint {
  font-size: var(--ds-text-size-body);
  color: var(--hm-text-secondary);
  text-align: center;
  margin: 0 0 20px;
  line-height: 1.5;
}

.sponsor-modal__form {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.sponsor-modal__field {
  position: relative;
}

.sponsor-modal__input {
  width: 100%;
  padding: 14px 16px;
  border: 1px solid var(--hm-border);
  border-radius: var(--p-radius-14);
  font-size: var(--p-text-15);
  color: var(--hm-text-primary);
  background: var(--ds-surface-canvas);
  transition: all 0.2s;
  box-sizing: border-box;

  &::placeholder {
    color: var(--hm-text-muted);
  }

  &:focus {
    outline: none;
    border-color: var(--hm-accent);
    background: var(--ds-text-on-accent);
    box-shadow: 0 0 0 4px var(--hm-accent-soft, var(--ds-accent-soft-08));
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
}

.sponsor-modal__error {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: var(--p-text-13);
  color: var(--hm-danger);
  margin: 0;

  i {
    font-size: var(--ds-text-size-body-lg);
  }
}

.sponsor-modal__button {
  width: 100%;
  padding: 14px;
  border: none;
  border-radius: var(--p-radius-50);
  font-size: var(--p-text-15);
  font-weight: var(--ds-weight-semibold);
  color: white;
  background: var(--hm-accent);
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  &:hover:not(:disabled) {
    background: var(--ds-accent);
  }
}

@keyframes slideIn {
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
